import React, { Suspense, useRef, useCallback } from 'react'
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types'

const LazyExcalidrawWithMenu = React.lazy(() =>
  import('@excalidraw/excalidraw').then(async (mod) => {
    await import('@excalidraw/excalidraw/index.css')
    const { Excalidraw, MainMenu } = mod
    const Wrapper = (props: any) => (
      <Excalidraw {...props}>
        <MainMenu>
          <MainMenu.DefaultItems.SearchMenu />
          <MainMenu.DefaultItems.ClearCanvas />
          <MainMenu.Separator />
          <MainMenu.DefaultItems.ChangeCanvasBackground />
        </MainMenu>
        {props.children}
      </Excalidraw>
    )
    return { default: Wrapper }
  })
)

export interface CanvasSelectedElement {
  id: string
  type: string
  text?: string
}

interface ExcalidrawCanvasProps {
  onAPIReady: (api: ExcalidrawImperativeAPI) => void
  initialData?: { elements: readonly any[]; files?: Record<string, any> }
  onChange?: (elements: readonly any[]) => void
  onSelectionChange?: (elements: CanvasSelectedElement[]) => void
  viewModeEnabled?: boolean
}

function elementFingerprint(elements: readonly any[]): string {
  const visible = elements.filter((e) => !e.isDeleted)
  return visible.map((e) => `${e.id}:${e.version}`).join(',')
}

export function ExcalidrawCanvas({ onAPIReady, initialData, onChange, onSelectionChange, viewModeEnabled = false }: ExcalidrawCanvasProps) {
  const isInitialLoadRef = useRef(true)
  const lastFingerprintRef = useRef('')
  const lastSelectionKeyRef = useRef('')
  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const handleChange = useCallback(
    (elements: readonly any[], appState?: any) => {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false
        lastFingerprintRef.current = elementFingerprint(elements)
        return
      }

      // Check for element content changes (existing behavior)
      const fp = elementFingerprint(elements)
      if (fp !== lastFingerprintRef.current) {
        lastFingerprintRef.current = fp
        onChange?.(elements)
      }

      // Check for selection changes
      if (appState && onSelectionChangeRef.current) {
        const selectedIds = appState.selectedElementIds || {}
        const selectionKey = Object.keys(selectedIds).filter(id => selectedIds[id]).sort().join(',')

        if (selectionKey !== lastSelectionKeyRef.current) {
          lastSelectionKeyRef.current = selectionKey

          if (selectionKey === '') {
            onSelectionChangeRef.current([])
          } else {
            const selected = elements
              .filter((el: any) => selectedIds[el.id] && !el.isDeleted)
              .map((el: any) => ({
                id: el.id,
                type: el.type,
                text: el.type === 'text' ? el.text : undefined,
              }))
            onSelectionChangeRef.current(selected)
          }
        }
      }
    },
    [onChange]
  )

  const mergedInitialData = {
    ...initialData,
    appState: { activeTool: { type: 'hand' as const }, currentItemRoughness: 0 },
  }

  return (
    <div className="h-full w-full excalidraw-canvas">
      <style>{`
        .excalidraw-canvas .help-icon { display: none !important; }
        .excalidraw-canvas .default-sidebar-trigger { display: none !important; }
        .excalidraw-canvas .sidebar-triggers .sidebar-tab-trigger:nth-child(2) { display: none !important; }
        .HelpDialog__header { display: none !important; }
        .excalidraw-canvas [data-testid="toolbar-embeddable"],
        .excalidraw-canvas .dropdown-menu-container div[style*="font-weight"] { display: none !important; }
      `}</style>
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center text-muted-foreground">
            Loading canvas...
          </div>
        }
      >
        <LazyExcalidrawWithMenu
          excalidrawAPI={onAPIReady}
          initialData={mergedInitialData}
          onChange={handleChange}
          viewModeEnabled={viewModeEnabled}
          theme="light"
          UIOptions={{
            canvasActions: {
              clearCanvas: true,
              export: false,
              saveAsImage: false,
              loadScene: false,
              toggleTheme: false,
            },
            tools: {
              image: false,
            },
          }}
        />
      </Suspense>
    </div>
  )
}
